import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { SpeedInsights } from '@vercel/speed-insights/react';
import styled from 'styled-components';
import { AuthProvider } from './hooks/useAuth';
import StickyNavbar from './components/StickyNavbar';
import Footer from './components/Footer';
import WhatsApp from './components/WhatsApp';
import ScrollToTop from './components/ScrollToTop';
import MouseShadow from './components/MouseShadow';
import GoogleAnalytics from './components/GoogleAnalytics';
import ServerOptimization from './components/ServerOptimization';
import ErrorBoundary from './components/ErrorBoundary';
import Home from './pages/Home';
import About from './pages/company/About';
import Products from './pages/Products';
import Blogs from './pages/Blogs';
import Contact from './pages/company/Contact';
import Services from './pages/Services';
import Quote from './pages/Quote';
import Careers from './pages/company/Careers';
import JobApplication from './pages/company/JobApplication';
import ApplicationReview from './pages/company/ApplicationReview';
import LogoShowcase from './pages/LogoShowcase';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Cookies from './pages/Cookies';
import ProfessionalTeam from './components/ProfessionalTeam';
import ArchitecturalDesign2026 from './pages/blogposts/ArchitecturalDesign2026';
import SEOStrategies2026 from './pages/blogposts/SEOStrategies2026';
import ProfessionalWebsite2026 from './pages/blogposts/ProfessionalWebsite2026';
import AIArchitecture2026 from './pages/blogposts/AIArchitecture2026';
import MERNStack2026 from './pages/blogposts/MERNStack2026';
import Microservices2026 from './pages/blogposts/Microservices2026';
import VibeCoding2026 from './pages/blogposts/VibeCoding2026';
import VibeCoding2_0 from './pages/blogposts/VibeCoding2_0';
import Connect from './pages/Connect';
import GlobalStyles from './utils/GlobalStyles';

const TricolorLine = styled.div`
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  height: 6px;
  background: linear-gradient(90deg, 
    #ff9933 0%, 
    #ff9933 33.33%, 
    #ffffff 33.33%, 
    #ffffff 66.66%, 
    #138808 66.66%, 
    #138808 100%
  );
  background-size: 300% 100%;
  animation: tricolorFlow 4s linear infinite;
  z-index: 999;
  box-shadow: 
    0 0 15px rgba(255, 153, 51, 0.8),
    0 0 15px rgba(255, 255, 255, 0.6),
    0 0 15px rgba(19, 136, 8, 0.8),
    0 2px 10px rgba(0, 0, 0, 0.3);
  
  @keyframes tricolorFlow {
    0% { background-position: 0% 0%; }
    100% { background-position: 300% 0%; }
  }
`;

// Component to handle scroll to top on route change
function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const isPrerender = typeof navigator !== 'undefined' && navigator.userAgent?.toLowerCase().includes('reactsnap');
  return (
    <ErrorBoundary>
      <AuthProvider>
          <Router>
            <ServerOptimization />
            <GlobalStyles />
          <GoogleAnalytics measurementId="G-INITX2024TECH" />
          <AppContent isPrerender={isPrerender} />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

function AppContent({ isPrerender }) {
  const { pathname } = useLocation();
  const isConnectPage = pathname === '/connect';
  return (
    <>
      {!isConnectPage && <StickyNavbar />}
      <ScrollToTopOnRouteChange />
      <div style={{ paddingTop: isConnectPage ? '0' : '45px' }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/quote" element={<Quote />} />
          <Route path="/products" element={<Products />} />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/apply" element={<JobApplication />} />
          <Route path="/review" element={<ApplicationReview />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/logo" element={<LogoShowcase />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/ourteam" element={<ProfessionalTeam />} />
          <Route path="/team" element={<ProfessionalTeam />} />
          <Route path="/connect" element={<Connect />} />
          <Route path="/blog/architectural-design-2026" element={<ArchitecturalDesign2026 />} />
          <Route path="/blog/seo-strategies-2026" element={<SEOStrategies2026 />} />
          <Route path="/blog/professional-website-2026" element={<ProfessionalWebsite2026 />} />
          <Route path="/blog/ai-solutions-architecture" element={<AIArchitecture2026 />} />
          <Route path="/blog/mern-stack-2026" element={<MERNStack2026 />} />
          <Route path="/blog/microservices-architecture" element={<Microservices2026 />} />
          <Route path="/blog/vibe-coding-2026" element={<VibeCoding2026 />} />
          <Route path="/blog/vibe-coding-2-0" element={<VibeCoding2_0 />} />
        </Routes>
      </div>
      {!isConnectPage && <Footer />}
      {!isConnectPage && <WhatsApp />}
      {!isConnectPage && <ScrollToTop />}
      {!isConnectPage && <MouseShadow />}
      {!isPrerender && <SpeedInsights />}
    </>
  );
}

export default App;
